import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';

const EnquiryModal = ({ isOpen, onClose, lawyer, userData }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    legalArea: '',
    description: ''
  });
  const [status, setStatus] = useState('idle');
  const [preFilledFields, setPreFilledFields] = useState({
    name: false,
    phone: false
  });

  // Pre-fill form with user data if available
  useEffect(() => {
    if (userData) {
      const newPreFilledFields = { ...preFilledFields };
      const newFormData = { ...formData };
      
      if (userData.name || userData.fullName) {
        newFormData.name = userData.name || userData.fullName || '';
        newPreFilledFields.name = true;
      }
      
      if (userData.phone) {
        newFormData.phone = userData.phone || '';
        newPreFilledFields.phone = true;
      }
      
      setFormData(newFormData);
      setPreFilledFields(newPreFilledFields);
    }
  }, [userData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Only update if field is not pre-filled
    if (!preFilledFields[name]) {
      setFormData(prevState => ({
        ...prevState,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('sending');
    
    try {
      // Get lawyer email from contactDetails if available, otherwise fall back to email property
      // or use the default Zahoot email as a last resort
      const lawyerEmail = lawyer?.contactDetails?.email && lawyer.contactDetails.email !== "Not available" 
        ? lawyer.contactDetails.email 
        : (lawyer?.email || 'info@zahoot.co.uk');
      
      // The user's email is now retrieved from their authentication session
      await axios.post(`${process.env.REACT_APP_API_URL || ''}/api/enquiries/send`, {
        ...formData,
        lawyerId: lawyer?.id,
        lawyerName: lawyer?.name,
        lawyerEmail: lawyerEmail
      }, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      setStatus('success');
      toast.success('Enquiry sent successfully! The solicitor will receive your details if they claim the listing.');
      
      // Reset form data
      setFormData({
        name: '',
        phone: '',
        legalArea: '',
        description: ''
      });
      
      // Close the modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (error) {
      console.error('Error sending enquiry:', error);
      setStatus('error');
      
      if (error.response?.status === 401) {
        toast.error('You must be logged in to send an enquiry. Please sign in and try again.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to send enquiry');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-center justify-center min-h-screen px-4 text-center sm:block sm:p-0">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={onClose}></div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

        <div className="inline-block align-bottom bg-gradient-to-r from-[#301555] to-[#411C73] rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="px-6 pt-5 pb-6 sticky top-0 z-10">
            <div className="flex items-center justify-between">
              <h3 className="text-2xl font-bold text-white" id="modal-title">
                Send Enquiry
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-white"
                onClick={onClose}
              >
                <span className="sr-only">Close</span>
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <p className="mt-2 text-gray-300">
              Fill in your details to send an enquiry to {lawyer?.name || 'the solicitor'}. Your contact information will be shared with the solicitor only after they claim their listing in our system.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-4 max-h-[70vh] overflow-y-auto">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-gray-300">
                Full Name {preFilledFields.name && <span className="ml-1 text-xs text-gray-400">(Pre-filled)</span>}
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                disabled={preFilledFields.name}
                className={`mt-1 block w-full rounded-md border border-purple-700/30 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 bg-purple-900/30 text-white px-3 py-2 ${preFilledFields.name ? 'opacity-80' : ''}`}
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-gray-300">
                Phone Number {preFilledFields.phone && <span className="ml-1 text-xs text-gray-400">(Pre-filled)</span>}
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                required
                value={formData.phone}
                onChange={handleChange}
                disabled={preFilledFields.phone}
                className={`mt-1 block w-full rounded-md border border-purple-700/30 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 bg-purple-900/30 text-white px-3 py-2 ${preFilledFields.phone ? 'opacity-80' : ''}`}
              />
            </div>

            <div>
              <label htmlFor="legalArea" className="block text-sm font-medium text-gray-300">
                Legal Service Area
              </label>
              <input
                type="text"
                id="legalArea"
                name="legalArea"
                required
                value={formData.legalArea}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-purple-700/30 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 bg-purple-900/30 text-white px-3 py-2"
                placeholder="e.g. Family Law, Immigration, etc."
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-gray-300">
                Describe your legal needs
              </label>
              <textarea
                id="description"
                name="description"
                rows="4"
                required
                value={formData.description}
                onChange={handleChange}
                className="mt-1 block w-full rounded-md border border-purple-700/30 shadow-sm focus:border-purple-500 focus:ring focus:ring-purple-500 focus:ring-opacity-50 bg-purple-900/30 text-white px-3 py-2"
                placeholder="Briefly describe what you need help with..."
              />
            </div>

            <div className="mt-5 p-3 bg-amber-100 border border-amber-300 rounded-lg">
              <p className="text-amber-800 text-sm flex items-start">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0 text-amber-500" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
                <span>
                  <strong>Please Note!</strong> This enquiry will be shared with the solicitor. Do not include sensitive financial details or confidential information that requires legal privilege. The solicitor will contact you using the details provided. We will try to arrange a response close to your preferred time, however this is subject to solicitor availability.
                </span>
              </p>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-full hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 mr-3"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={status === 'sending'}
                className="px-4 py-2 bg-gradient-to-r from-[#D80364] to-[#F0654B] text-white text-sm font-medium rounded-full hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
              >
                {status === 'sending' ? 'Sending...' : 'Send Enquiry'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EnquiryModal; 